import { useEffect, useLayoutEffect } from 'react';
import { TileLayer, useMap } from 'react-leaflet';
import { Globe, Moon, Map } from 'lucide-react';

/**
 * FE-27 — piezas del mapa base que comparten `MapView` (Monitoreo) y
 * `RouteHistoryMap` (página propia del histórico en mobile): tiles, selector
 * de estilo y el ajuste de tamaño. Se extrajeron de `MapView` tal cual para no
 * mantener dos copias de los tiles ni de su historia (issue #120).
 */

export type MapStyleMode = 'satellite' | 'dark' | 'streets';

// 🚨 "dark" y "streets" NO usan CARTO (`basemaps.cartocdn.com`) — issue #120:
// CARTO exige API key para sus basemaps anónimos desde su cambio de política,
// y sin ella sirve un PNG válido (200 OK) con la marca de agua "API KEY
// REQUIRED" en vez de un error — por eso no se veía en la consola ni en la
// red como un fallo. Medido contra los CUATRO dominios de CARTO (el actual
// y el legado `*.global.ssl.fastly.net`, con y sin `@2x`): los cuatro están
// igual de bloqueados. La reunión lo atribuyó a "Google Maps" por error —
// este mapa nunca ha usado Google Maps ni tiene su clave configurada en
// ninguna parte (`grep` de `google.maps`/`GOOGLE_MAPS` en `src/` no devuelve
// nada).
//
// Se reemplazan por OpenStreetMap estándar (gratis, sin key, política de uso
// razonable — de sobra para este volumen). "Oscuro" reusa el mismo tile con
// un filtro CSS (`.map-tile--dark`, `styles/index.css`) en vez de un tile
// vectorial oscuro dedicado: evita depender de OTRO proveedor con clave
// (Stadia/Mapbox) por un problema puramente estético.
const TILES_CONFIG = {
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "&copy; Esri World Imagery Satellite",
    overlay: "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}",
    className: undefined as string | undefined,
  },
  dark: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "&copy; OpenStreetMap contributors",
    overlay: null,
    className: "map-tile--dark",
  },
  streets: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "&copy; OpenStreetMap contributors",
    overlay: null,
    className: undefined as string | undefined,
  },
};

/** Los tiles del estilo activo. Va dentro de un `MapContainer`. */
export function MapTiles({ mapStyle }: { mapStyle: MapStyleMode }) {
  const activeTile = TILES_CONFIG[mapStyle];
  return (
    <>
      <TileLayer
        key={mapStyle}
        attribution={activeTile.attribution}
        url={activeTile.url}
        className={activeTile.className}
      />
      {activeTile.overlay && (
        <TileLayer
          url={activeTile.overlay}
          opacity={0.7}
        />
      )}
    </>
  );
}

const STYLE_OPTIONS: { mode: MapStyleMode; title: string; label: string; icon: React.ReactNode }[] = [
  { mode: 'satellite', title: 'Satelital Híbrido HD (Esri World)', label: 'Satelital', icon: <Globe size={13} /> },
  { mode: 'dark', title: 'Mapa Vectorial Oscuro', label: 'Oscuro', icon: <Moon size={13} /> },
  { mode: 'streets', title: 'Mapa Callejero HD', label: 'Callejero', icon: <Map size={13} /> },
];

/** Selector de estilo flotando arriba a la derecha del contenedor posicionado. */
export function MapStyleSelector({ mapStyle, onChange }: { mapStyle: MapStyleMode; onChange: (mode: MapStyleMode) => void }) {
  return (
    <div style={{
      position: 'absolute',
      top: '12px',
      right: '12px',
      zIndex: 1000,
      background: 'rgba(15, 23, 42, 0.85)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      borderRadius: 'var(--radius-lg)',
      padding: '4px',
      display: 'flex',
      gap: '4px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
    }}>
      {STYLE_OPTIONS.map(opt => (
        <button
          key={opt.mode}
          onClick={() => onChange(opt.mode)}
          title={opt.title}
          style={{
            padding: '4px 10px',
            borderRadius: '6px',
            border: 'none',
            background: mapStyle === opt.mode ? 'var(--accent)' : 'transparent',
            color: '#FFF',
            fontSize: '11px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s ease',
          }}
        >
          {opt.icon} {opt.label}
        </button>
      ))}
    </div>
  );
}

export function MapResizeHandler() {
  const map = useMap();

  useLayoutEffect(() => {
    map.invalidateSize();
  }, [map]);

  useEffect(() => {
    map.invalidateSize();
    const timers = [
      setTimeout(() => map.invalidateSize(), 50),
      setTimeout(() => map.invalidateSize(), 150),
      setTimeout(() => map.invalidateSize(), 400),
      setTimeout(() => map.invalidateSize(), 800),
    ];

    const handleResize = () => {
      map.invalidateSize();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    const container = map.getContainer();
    let observer: ResizeObserver | null = null;

    if (container) {
      observer = new ResizeObserver(() => {
        map.invalidateSize();
      });
      observer.observe(container);
    }

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (observer) observer.disconnect();
    };
  }, [map]);

  return null;
}
